<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="내 현장 정보"
        subtitle="현재 소속된 현장의 상세 정보"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <!-- 로딩 -->
        <div v-if="loading" class="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center justify-center">
          <div class="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p class="text-[14px] text-slate-400">정보를 불러오는 중...</p>
        </div>

        <!-- 소속 현장이 없는 경우 -->
        <div v-else-if="!currentAffiliation" class="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center justify-center">
          <div class="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
          </div>
          <p class="text-[15px] font-medium text-slate-500 mb-1">소속된 현장이 없습니다</p>
          <p class="text-[13px] text-slate-400 mb-4">현장 변경 신청 메뉴에서 현장을 신청해주세요</p>
          <router-link to="/partners/property/change" class="px-5 py-2.5 bg-blue-600 text-white text-[14px] font-bold rounded-xl hover:bg-blue-700 transition flex items-center gap-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            현장 신청하기
          </router-link>
        </div>

        <!-- 소속 현장 정보 -->
        <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 좌측: 내 소속 정보 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 소속 정보 카드 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-5">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-[14px] font-bold text-slate-900">내 소속 정보</p>
                  <p class="text-[12px] text-slate-400">영업 활동 정보</p>
                </div>
              </div>

              <div class="space-y-3">
                <div class="flex items-center justify-between py-2 border-b border-slate-100">
                  <span class="text-[13px] text-slate-500">소속 승인일</span>
                  <span class="text-[13px] font-medium text-slate-800">{{ formatDate(currentAffiliation.approvedAt) }}</span>
                </div>
                <div class="flex items-center justify-between py-2 border-b border-slate-100">
                  <span class="text-[13px] text-slate-500">활동 기간</span>
                  <span class="text-[13px] font-bold text-blue-600">{{ getDaysActive }}일</span>
                </div>
                <div class="flex items-center justify-between py-2">
                  <span class="text-[13px] text-slate-500">다음 변경 가능일</span>
                  <span v-if="canChangeAffiliation" class="text-[13px] font-medium text-emerald-600">변경 가능</span>
                  <span v-else class="text-[13px] font-medium text-slate-800">{{ daysUntilChange }}일 후</span>
                </div>
              </div>
            </div>

            <!-- 영업 정보 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-5">
              <p class="text-[13px] font-bold text-slate-500 mb-3">영업 정보</p>
              <div class="space-y-3">
                <div class="bg-slate-50 rounded-xl p-3">
                  <p class="text-[11px] text-slate-400 mb-1">영업명</p>
                  <p class="text-[14px] font-bold text-slate-900">{{ currentAffiliation.displayName }}</p>
                </div>
                <div class="bg-slate-50 rounded-xl p-3">
                  <p class="text-[11px] text-slate-400 mb-1">한 줄 소개</p>
                  <p class="text-[14px] font-medium text-slate-800">{{ currentAffiliation.introduction }}</p>
                </div>
              </div>
            </div>

            <!-- 바로가기 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-4">
              <p class="text-[12px] font-bold text-slate-500 mb-3">바로가기</p>
              <div class="space-y-2">
                <router-link to="/partners/property/change" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                      </svg>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">현장 변경 신청</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
                <router-link to="/partners/property/history" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                      </svg>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">심사 결과 확인</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- 우측: 현장 상세 정보 -->
          <div class="lg:col-span-2 space-y-4">
            <!-- 현장 기본 정보 -->
            <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div class="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <p class="text-[14px] font-bold text-slate-900">현장 정보</p>
                <span :class="[
                  'px-3 py-1 rounded-full text-[12px] font-bold',
                  propertyData?.status === 'on-sale' ? 'bg-emerald-100 text-emerald-700' :
                  propertyData?.status === 'pre-sale' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-600'
                ]">
                  {{ getStatusLabel(propertyData?.status) }}
                </span>
              </div>

              <div class="p-5">
                <div class="flex gap-5 mb-5">
                  <div v-if="propertyData?.thumbnailUrl" class="w-28 h-28 rounded-xl overflow-hidden flex-shrink-0">
                    <img :src="propertyData.thumbnailUrl" :alt="propertyData.name" class="w-full h-full object-cover" />
                  </div>
                  <div v-else class="w-28 h-28 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <h3 class="text-[18px] font-bold text-slate-900 mb-2">{{ currentAffiliation.propertyName }}</h3>
                    <p class="flex items-center gap-1 text-[13px] text-slate-500">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                      {{ propertyData?.address || '-' }}
                    </p>
                  </div>
                </div>

                <div class="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl mb-5">
                  <div class="text-center">
                    <p class="text-[11px] text-slate-400 mb-1">시행/시공사</p>
                    <p class="text-[13px] font-bold text-slate-800">{{ propertyData?.developer || '-' }}</p>
                  </div>
                  <div class="text-center">
                    <p class="text-[11px] text-slate-400 mb-1">총 세대수</p>
                    <p class="text-[13px] font-bold text-slate-800">{{ propertyData?.totalUnits || '-' }}세대</p>
                  </div>
                  <div class="text-center">
                    <p class="text-[11px] text-slate-400 mb-1">현장 등록일</p>
                    <p class="text-[13px] font-bold text-slate-800">{{ formatDate(propertyData?.createdAt) }}</p>
                  </div>
                </div>

                <div v-if="propertyData?.description" class="p-4 bg-blue-50 rounded-xl">
                  <p class="text-[12px] font-bold text-blue-800 mb-2">현장 설명</p>
                  <p class="text-[13px] text-blue-700 leading-relaxed">{{ propertyData.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

export default {
  name: 'MyPropertyView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const { user } = useAuth()
    const loading = ref(true)
    const currentAffiliation = ref(null)
    const propertyData = ref(null)

    const fetchMyAffiliation = async () => {
      if (!user.value) return

      try {
        const q = query(
          collection(db, 'partnerApplications'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'approved')
        )
        const snapshot = await getDocs(q)

        if (!snapshot.empty) {
          const appDoc = snapshot.docs[0]
          currentAffiliation.value = {
            id: appDoc.id,
            ...appDoc.data()
          }

          const propertyDoc = await getDoc(doc(db, 'properties', currentAffiliation.value.propertyId))
          if (propertyDoc.exists()) {
            propertyData.value = {
              id: propertyDoc.id,
              ...propertyDoc.data()
            }
          }
        }
      } catch (error) {
        console.error('소속 정보 로드 실패:', error)
      }
    }

    const getDaysActive = computed(() => {
      if (!currentAffiliation.value?.approvedAt) return 0
      const approved = new Date(currentAffiliation.value.approvedAt)
      const now = new Date()
      return Math.floor((now - approved) / (1000 * 60 * 60 * 24))
    })

    const canChangeAffiliation = computed(() => {
      if (!currentAffiliation.value?.approvedAt) return true
      const approved = new Date(currentAffiliation.value.approvedAt)
      const now = new Date()
      const diffDays = Math.floor((now - approved) / (1000 * 60 * 60 * 24))
      return diffDays >= 30
    })

    const daysUntilChange = computed(() => {
      if (!currentAffiliation.value?.approvedAt) return 0
      const approved = new Date(currentAffiliation.value.approvedAt)
      const now = new Date()
      const diffDays = Math.floor((now - approved) / (1000 * 60 * 60 * 24))
      return Math.max(0, 30 - diffDays)
    })

    const getStatusLabel = (status) => {
      const labels = {
        'pre-sale': '분양예정',
        'on-sale': '분양중',
        'completed': '분양완료'
      }
      return labels[status] || status
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    onMounted(async () => {
      loading.value = true
      await fetchMyAffiliation()
      loading.value = false
    })

    return {
      loading,
      currentAffiliation,
      propertyData,
      getDaysActive,
      canChangeAffiliation,
      daysUntilChange,
      getStatusLabel,
      formatDate
    }
  }
}
</script>
